import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '../../dronebee-ui';
import { useGetApplicationsQuery } from '../redux/reducers/application';
import { useLogoutMutation } from '../redux/reducers/auth';
import { useGetMessagesQuery } from '../redux/reducers/messages';
import LogoutModal from './auth/LogoutModal';
import { useToggle } from '../hooks';

const config = programConfig;

const NavBar = ({ isLoggedIn, isBannerVisible, isTranslatorOpened }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Extract application_uuid from pathname
  const pathParts = pathname.split('/');
  let application_uuid = null;

  if (pathParts.includes('application')) {
    application_uuid = pathParts[pathParts.indexOf('application') + 1];
  } else if (pathParts.includes('message-center')) {
    application_uuid = pathParts[pathParts.indexOf('message-center') + 1];
  } else if (pathParts.includes('faq')) {
    application_uuid = pathParts[pathParts.indexOf('faq') + 1];
  } else if (pathParts.includes('required-documents')) {
    application_uuid = pathParts[pathParts.indexOf('required-documents') + 1];
  }

  const { data: userApplications = {} } = useGetApplicationsQuery(undefined, { skip: !isLoggedIn, refetchOnMountOrArgChange: true });
  const { applications = [] } = userApplications;
  const hasMultiApplication = applications?.length > 1;

  // Ensure application_uuid is set for single application users
  application_uuid = application_uuid || (applications.length === 1 ? applications[0].application_uuid : null);


  const { data: messages = [] } = useGetMessagesQuery(
    { application_uuid },
    {
      skip: !isLoggedIn || !application_uuid || hasMultiApplication,
      pollingInterval: 120000, // refresh every 2 minutes
    },
  );

  const [logout] = useLogoutMutation();

  const [activeDropdown, setActiveDropdown] = useState(false);
  const [isLogoutModalOpen, toggleLogoutModal] = useToggle();

  const showLogin = config.topNav.isLoginBtnVisible;

  const renderResources = (config, isDesktop) => {
    if (!config.staticPages.resources?.isNavBar) return null;

    let resources;

    if (isDesktop) {
      resources = (
        <li className={`nav-item d-none d-lg-flex justify-content-center ${pathname === '/resources' ? 'active' : ''}`}>
          <Button
            ghost
            className='px-0 px-lg-3 nav-link d-flex align-items-center justify-content-center'
            data-cy='resources'
            onClick={() => {
              config.staticPages.resources.isExternalLink
                ? window.open(config.staticPages.resources.externalURL, '_blank')
                : navigate('/resources');
            }}
          >
            <div>{config.staticPages.resources.title || 'Resources'}</div>
          </Button>
        </li>
      );
    } else {
      resources = (
        <>
          <div className='dropdown-item' data-cy='mobile-resources'
            onClick={() => {
              setActiveDropdown(false);
              config.staticPages.resources.isExternalLink
                ? window.open(config.staticPages.resources.externalURL, '_blank')
                : navigate('/resources');
            }}>
            <div className='body p-3 nav-mobile-title ps-3'><span>{config.staticPages.resources.title || 'Resources'}</span></div>
          </div>
          <div className='dropdown-divider'></div>
        </>
      );
    }

    if (isLoggedIn && config.staticPages.resources.isVisible.includes('loggedIn')) {
      return resources;
    }

    if (!isLoggedIn && config.staticPages.resources.isVisible.includes('loggedOut')) {
      return resources;
    }

    return null;
  };

  return (
    <>
      <nav className='navbar col-12 p-0 fixed-top d-flex flex-row' style={isBannerVisible ? { marginTop: '60px' } : {}}>
        <div className='navbar-menu-wrapper d-flex align-items-center justify-content-between' style={{ marginTop: isTranslatorOpened ? '37px' : '' }}>
          <div className='ms-3 pointer' data-cy='nav-logo' onClick={() => navigate('/')}>
            <div className='mb-0'>
              <img src={config.style.logo} style={{ maxHeight: '60px', maxWidth: '280px' }} alt='top nav logo' />
            </div>
          </div>
          {/* ----------- DESKTOP ----------- */}
          <ul className='navbar-nav'>
            {/* SKIP NAVIGATION */}
            <Button
              ghost
              size='xs'
              className='skip-link text-black f-regular mx-3 sr-only'
              data-cy='skip-nav'
              onClick={(e) => {
                e.preventDefault();
                const container = document.getElementById('app-wrapper');
                if (container) {
                  container.tabIndex = -1;
                  container.focus();
                  setTimeout(() => container.removeAttribute('tabindex'), 1000);
                }
              }}
            >Skip Navigation</Button>

            {/* RESOURCES */}
            {renderResources(config, true)}

            {/* FAQ */}
            {!isLoggedIn && config.faqPage.isVisible &&
              <li className={`nav-item d-none d-lg-flex justify-content-center ${pathname === '/faq' ? 'active' : ''}`} >
                <Button
                  ghost
                  className='px-0 px-lg-3 nav-link d-flex align-items-center justify-content-center'
                  data-cy='faq'
                  onClick={() => {
                    config.faqPage.isExternalLink
                      ? window.open(config.faqPage.externalURL, '_blank')
                      : navigate('/faq');
                  }}
                >
                  {/* <div className={`border-bottom border-3 p-2 ${pathname.includes('/faq') ? 'border-primary' : ''}`}> */}
                  <div>FAQs</div>
                  {/* </div> */}
                </Button>
              </li>
            }

            {/* REQUIRED DOCUMENTS */}
            {!isLoggedIn && config.requiredDocumentsPage.isVisible &&
              <li className={`nav-item d-none d-lg-flex justify-content-center ${pathname === '/required-documents' ? 'active' : ''}`}>
                <Button
                  ghost
                  className='px-0 px-lg-3 nav-link d-flex align-items-center justify-content-center'
                  data-cy='required-documents'
                  onClick={() => navigate('/required-documents')}
                >
                  {/* <div className={`border-bottom border-3 p-2 ${pathname.includes('/required-documents') ? 'border-primary' : ''}`}> */}
                  <div>Required Documents</div>
                  {/* </div> */}
                </Button>
              </li>
            }

            {/* MY APPLICATION */}
            {isLoggedIn &&
              <li className={`nav-item d-none d-lg-flex justify-content-center ${pathname.includes('/application') ? 'active' : ''}`}>
                <Button
                  ghost
                  className='px-0 px-lg-3 nav-link d-flex align-items-center justify-content-center'
                  data-cy='my-application'
                  onClick={() => {
                    // Intentional blank to route to application page
                    navigate('/');
                  }}
                >
                  <div className={`border-bottom border-3 p-2 ${pathname.includes('/application') ? 'border-primary' : ''}`}>
                    <span className='fas fa-file-pen me-2' />{!hasMultiApplication ? 'My Application' : 'My Applications'}
                  </div>
                </Button>
              </li>
            }

            {/* MESSAGE CENTER */}
            {isLoggedIn && !hasMultiApplication &&
              <li className={`nav-item d-none d-lg-flex justify-content-center ${pathname === `/message-center/${application_uuid}` ? 'active' : ''}`}>
                <Button
                  ghost
                  className='px-0 px-lg-3 nav-link d-flex align-items-center justify-content-center'
                  data-cy='message-center'
                  onClick={() => navigate(`/message-center/${application_uuid}`)}
                >
                  <div className={`border-bottom border-3 p-2 ${pathname.includes(`/message-center/${application_uuid}`) ? 'border-primary' : ''}`}>
                    <span className='fas fa-messages me-2' data-count={messages?.filter((d) => !d.is_read && d.sender_type === 'processor').length === 0 ? null : messages?.filter((d) => !d.is_read && d.sender_type === 'processor').length} />
                    Message Center
                  </div>
                </Button>
              </li>
            }

            {/* LOG IN/OUT */}
            {showLogin
              ? isLoggedIn
                ? <li className='nav-link px-3 d-none d-lg-flex'>
                  <Button
                    outline
                    size='sm'
                    className='mx-1 mx-sm-3'
                    data-cy='logout'
                    onClick={toggleLogoutModal}
                  >
                    Logout
                  </Button>
                </li>
                : <li className='nav-link px-3 d-none d-lg-flex'>
                  <Button
                    variant='success'
                    size='sm'
                    className='mx-1 mx-sm-3 text-white'
                    data-cy='login'
                    onClick={() => navigate('/login')}
                  >
                    Login
                  </Button>
                </li>
              : null
            }

            {/* ----------- TABLET/MOBILE ----------- */}
            <li className={`nav-item dropdown ${activeDropdown ? 'show selected' : ''} d-flex d-lg-none justify-content-center align-items-center`}>
              <div className='nav-link mobile-menu pointer' data-cy='mobile-dropdown' onClick={() => setActiveDropdown(!activeDropdown)}>
                <span className='nav-icon fas fa-bars' />
              </div>

              <div className={`navbar-dropdown dropdown-menu shadow fh-regular pointer ${activeDropdown ? 'show' : ''}`}>

                {/* RESOURCES */}
                {renderResources(config, false)}

                {/* FAQ */}
                {!isLoggedIn && config.faqPage.isVisible &&
                  <>
                    <div
                      className='dropdown-item'
                      data-cy='mobile-faq'
                      onClick={() => {
                        setActiveDropdown(false);
                        config.faqPage.isExternalLink
                          ? window.open(config.faqPage.externalURL, '_blank')
                          : navigate('/faq');
                      }}
                    >
                      <div className='body p-3 nav-mobile-title ps-3'><span>FAQs</span></div>
                    </div>
                    <div className='dropdown-divider'></div>
                  </>
                }

                {/* REQUIRED DOCUMENTS */}
                {!isLoggedIn && config.requiredDocumentsPage.isVisible &&
                  <>
                    <div
                      className='dropdown-item'
                      data-cy='mobile-required-docs'
                      onClick={() => {
                        setActiveDropdown(false);
                        navigate('/required-documents');
                      }}
                    >
                      <div className='body p-3 nav-mobile-title ps-3'><span>Required Documents</span></div>
                    </div>
                    <div className='dropdown-divider'></div>
                  </>
                }

                {/* MY APPLICATION */}
                {isLoggedIn &&
                  <>
                    <div
                      className='dropdown-item'
                      data-cy='mobile-application'
                      onClick={() => {
                        setActiveDropdown(false);
                        navigate('/');
                      }}
                    >
                      <div className='body p-3 nav-mobile-title ps-3'><span>{!hasMultiApplication ? 'My Application' : 'My Applications'}</span></div>
                    </div>
                    <div className='dropdown-divider'></div>
                  </>
                }

                {/* MESSAGE CENTER */}
                {isLoggedIn && !hasMultiApplication &&
                  <>
                    <div
                      className='dropdown-item'
                      data-cy='mobile-message-center'
                      onClick={() => {
                        setActiveDropdown(false);
                        navigate(`/message-center/${application_uuid}`);
                      }}
                    >
                      <div className='body p-3 nav-mobile-title ps-3'><span>Message Center</span></div>
                    </div>
                    <div className='dropdown-divider'></div>
                  </>
                }

                {/* LOG IN/OUT */}
                {showLogin
                  ? isLoggedIn
                    ? (
                      <div
                        className='dropdown-item'
                        data-cy='mobile-logout'
                        onClick={() => {
                          setActiveDropdown(false);
                          toggleLogoutModal();
                        }}
                      >
                        <div className='no-hover w-100 m-3'>
                          <Button
                            block
                            size='sm'
                            className='px-5 ps-3'
                          >
                            Log Out
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className='dropdown-item'
                        data-cy='mobile-login'
                        onClick={() => {
                          setActiveDropdown(false);
                          navigate('/login');
                        }}
                      >
                        <div className='no-hover w-100 m-3'>
                          <Button
                            block
                            size='sm'
                            variant='success'
                            className='px-5 ps-3'
                          >
                            Log In
                          </Button>
                        </div>
                      </div>
                    )
                  : null
                }
              </div>
            </li>
          </ul>
        </div>
      </nav>
      <LogoutModal isOpen={isLogoutModalOpen} logout={logout} toggle={toggleLogoutModal} />
    </>
  );
};

NavBar.propTypes = {
  isLoggedIn: PropTypes.bool,
  isBannerVisible: PropTypes.bool,
  isTranslatorOpened: PropTypes.bool,
};

export default NavBar;
